import React, { useState } from 'react';
import { DollarSign, Calendar, FileText, StickyNote, Eye, Building2, Layers } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FilePreviewModal from '../ui/FilePreviewModal';
import { formatDate, formatCurrency } from '../../utils/dateUtils';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { useCurrency } from '../../context/CurrencyContext';
import { getCreatedByLabelKey } from '../../utils/invoiceCreatedBy';

const statusStyles = {
    paid: 'bg-status-paid-bg text-status-paid',
    pending: 'bg-status-pending-bg text-status-pending',
    overdue: 'bg-status-overdue-bg text-status-overdue',
    upcoming: 'bg-accent/10 text-accent',
};

const Row = ({ icon: Icon, label, children }) => (
    <div className="flex items-start gap-3 py-2.5 border-b border-surface-border last:border-0">
        <div className="flex items-center gap-1.5 w-32 shrink-0 text-text-muted">
            {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
            <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
        </div>
        <div className="flex-1 text-sm text-text-primary min-w-0">{children}</div>
    </div>
);

const FileRow = ({ icon: Icon, label, fileUid, previewLabel }) => {
    const [previewOpen, setPreviewOpen] = useState(false);

    if (!fileUid) {
        return (
            <Row icon={Icon} label={label}>
                <span className="text-text-muted">—</span>
            </Row>
        );
    }

    return (
        <>
            <Row icon={Icon} label={label}>
                <button
                    type="button"
                    onClick={() => setPreviewOpen(true)}
                    className="inline-flex items-center gap-1.5 text-accent hover:text-accent/80 transition-colors text-xs font-medium"
                >
                    <Eye className="w-3.5 h-3.5" />
                    View file
                </button>
            </Row>
            {previewOpen && (
                <FilePreviewModal
                    fileUid={fileUid}
                    label={previewLabel}
                    onClose={() => setPreviewOpen(false)}
                />
            )}
        </>
    );
};

const ViewInvoiceModal = ({ isOpen, onClose, invoice, status }) => {
    const { t } = useLanguage();
    const { currency } = useCurrency();
    if (!invoice) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={invoice.name || 'Invoice'}
            subtitle="Invoice details"
            size="medium"
            footer={
                <Button variant="outline" size="medium" onClick={onClose}>
                    {t(KEYS.COMMON_CLOSE)}
                </Button>
            }
        >
            <div className="divide-y divide-surface-border">
                {/* Status badge */}
                {(status || invoice.created_by) && (
                    <div className="pb-3 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                            {status ? (
                            <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${statusStyles[status]}`}>
                                {status}
                            </span>
                            ) : null}
                        </div>
                        <div className="inline-flex items-center gap-1.5 min-w-0 ml-auto">
                            <span className="text-[10px] text-text-muted uppercase tracking-wider whitespace-nowrap">
                                {t(KEYS.COMMON_CREATED_BY)}
                            </span>
                            <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider bg-surface-overlay text-text-secondary">
                                {t(getCreatedByLabelKey(invoice.created_by))}
                            </span>
                        </div>
                    </div>
                )}

                <Row icon={Building2} label={t(KEYS.COMMON_SUPPLIER)}>
                    {invoice.supplierName || <span className="text-text-muted">—</span>}
                </Row>

                <Row icon={Layers} label={t(KEYS.COMMON_SERVICE)}>
                    {invoice.serviceName || <span className="text-text-muted">—</span>}
                </Row>

                <Row icon={DollarSign} label={t(KEYS.COMMON_AMOUNT)}>
                    <span className="font-mono font-medium">
                        {invoice.amount != null ? formatCurrency(invoice.amount, currency) : <span className="text-text-muted">—</span>}
                    </span>
                </Row>

                <Row icon={Calendar} label={t(KEYS.COMMON_DATE_ISSUED)}>
                    {invoice.issue_date
                        ? <span className="font-mono text-xs">{formatDate(invoice.issue_date)}</span>
                        : <span className="text-text-muted">—</span>}
                </Row>

                <Row icon={Calendar} label={t(KEYS.COMMON_DATE_DUE)}>
                    {invoice.due_date
                        ? <span className="font-mono text-xs">{formatDate(invoice.due_date)}</span>
                        : <span className="text-text-muted">—</span>}
                </Row>

                <Row icon={Calendar} label={t(KEYS.COMMON_DATE_PAID)}>
                    {invoice.paid_date
                        ? <span className="font-mono text-xs">{formatDate(invoice.paid_date)}</span>
                        : <span className="text-text-muted">—</span>}
                </Row>

                <Row icon={StickyNote} label={t(KEYS.COMMON_NOTES)}>
                    {invoice.notes
                        ? <span className="whitespace-pre-wrap text-xs">{invoice.notes}</span>
                        : <span className="text-text-muted">—</span>}
                </Row>

                <FileRow
                    icon={FileText}
                    label="Invoice"
                    fileUid={invoice.fileUid}
                    previewLabel="Invoice File"
                />

                <FileRow
                    icon={FileText}
                    label="Proof"
                    fileUid={invoice.paymentProofFileUid}
                    previewLabel="Payment Proof"
                />
            </div>
        </Modal>
    );
};

export default ViewInvoiceModal;
